import React, { useContext, useEffect, useRef, useState } from "react";
import "./SigninSignup.css";
import { useNavigate } from "react-router-dom";
import { Context } from "../../context/Context";
import config from "../../config.json";
import { Button, Input, Card, NoticeBox } from "../../components/ui";
import Logo from "../../assets/logo/logo.svg";

const MIN_PASSWORD_LENGTH = 8;

const text = {
  ar: {
    title: "تعيين كلمة المرور",
    description: "اختر كلمة مرور خاصة بك لتفعيل حسابك في LUD",
    newPassword: "كلمة المرور الجديدة",
    confirmPassword: "تأكيد كلمة المرور",
    submit: "حفظ كلمة المرور",
    checking: "جارٍ التحقق من الرابط...",
    invalid: "هذا الرابط غير صالح أو انتهت صلاحيته. اطلب من فريق LUD إرسال دعوة جديدة.",
    tooShort: `كلمة المرور يجب ألا تقل عن ${MIN_PASSWORD_LENGTH} أحرف`,
    mismatch: "كلمتا المرور غير متطابقتين",
    failed: "تعذر حفظ كلمة المرور",
    done: "تم تعيين كلمة المرور بنجاح، يمكنك تسجيل الدخول الآن",
    signin: "تسجيل الدخول",
  },
  en: {
    title: "Set your password",
    description: "Choose your own password to activate your LUD account",
    newPassword: "New password",
    confirmPassword: "Confirm password",
    submit: "Save password",
    checking: "Checking your link...",
    invalid: "This link is invalid or has expired. Ask the LUD team to send a new invitation.",
    tooShort: `Password must contain at least ${MIN_PASSWORD_LENGTH} characters`,
    mismatch: "Passwords do not match",
    failed: "Unable to save the password",
    done: "Your password was set. You can sign in now",
    signin: "Sign in",
  },
};

// The invitation token is in the URL fragment (#token=...): it is read once, removed from the address bar and the
// history entry, and only ever sent in a POST body.
const readToken = () => {
  try {
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    return params.get("token") || "";
  } catch (error) {
    return "";
  }
};

export const SetupAccount = () => {
  const { lang } = useContext(Context);
  const t = text[lang] || text.ar;
  const api = config.BASE_AUTH_API;
  const navigate = useNavigate();
  const token = useRef(readToken());
  const [status, setStatus] = useState(token.current ? "checking" : "invalid"); // checking | ready | invalid | done
  const [form, setForm] = useState({ newPassword: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token.current) return undefined;
    try {
      window.history.replaceState(null, "", window.location.pathname);
    } catch (error) {
      // the address bar is cosmetic; the token already lives in the ref
    }
    let cancelled = false;
    fetch(`${api}account-setup/validate`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ token: token.current }) })
      .then((response) => !cancelled && setStatus(response.ok ? "ready" : "invalid"))
      .catch(() => !cancelled && setStatus("invalid"));
    return () => {
      cancelled = true;
    };
  }, [api]);

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    if (form.newPassword.length < MIN_PASSWORD_LENGTH) return setError(t.tooShort);
    if (form.newPassword !== form.confirmPassword) return setError(t.mismatch);
    setSubmitting(true);
    try {
      const response = await fetch(`${api}account-setup/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token.current, newPassword: form.newPassword, confirmPassword: form.confirmPassword }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        if (data.code === "SETUP_TOKEN_INVALID") setStatus("invalid");
        throw new Error(data.code === "SETUP_TOKEN_INVALID" ? t.invalid : data.message || t.failed);
      }
      token.current = "";
      setForm({ newPassword: "", confirmPassword: "" });
      setStatus("done");
    } catch (failure) {
      setError(failure.message || t.failed);
    } finally {
      setSubmitting(false);
    }
  };

  const change = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  return (
    <div className={`signinsignup ${lang === "ar" ? "ar" : "en"}`}>
      <Card className="signinsignup-container" hoverable={false}>
        <form className="signinsignup-form" onSubmit={submit}>
          <img src={Logo} alt="LUD" className="signinsignup-logo" />
          <h1>{t.title}</h1>

          {status === "checking" && <p className="signinsignup-forgot-description">{t.checking}</p>}

          {status === "invalid" && (
            <NoticeBox variant="danger" size="sm" className="signinsignup-notice">
              {t.invalid}
            </NoticeBox>
          )}

          {status === "ready" && (
            <>
              <p className="signinsignup-forgot-description">{t.description}</p>
              <div className="signinsignup-fields">
                <Input type="password" label={t.newPassword} placeholder={t.newPassword} name="newPassword" autoComplete="new-password" value={form.newPassword} onChange={change} required fullWidth />
                <Input type="password" label={t.confirmPassword} placeholder={t.confirmPassword} name="confirmPassword" autoComplete="new-password" value={form.confirmPassword} onChange={change} required fullWidth />
              </div>
              {error && (
                <NoticeBox variant="danger" size="sm" className="signinsignup-notice">
                  {error}
                </NoticeBox>
              )}
              <Button type="submit" variant="primary" appearance="filled" size="lg" fullWidth loading={submitting} className="signinsignup-submit">
                {t.submit}
              </Button>
            </>
          )}

          {status === "done" && (
            <>
              <NoticeBox variant="success" size="sm" className="signinsignup-notice">
                {t.done}
              </NoticeBox>
              <Button type="button" variant="primary" appearance="filled" size="lg" fullWidth className="signinsignup-submit" onClick={() => navigate("/signin")}>
                {t.signin}
              </Button>
            </>
          )}
        </form>
      </Card>
    </div>
  );
};
